import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flex, message, Typography } from 'antd';
import { PartitionOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import './InstallationLocationDetailPage.scss';
import { useTranslation } from '../../i18n/i18n';
import CreateInstallation from '../../components/installation-location/InstallationLocationDetail/CreateInstallation';
import InstallationTable, { InstallationTableItem } from '../../components/installation/InstallationTable';
import InstallationLocationDetailInfo from '../../components/installation-location/InstallationLocationDetail/InstallationLocationDetailInfo';
import { UpdateLocationParams } from '../../components/installation-location/InstallationLocationDetail/EditInstallationLocationModal';
import { dashboardPath, installationDetailPath, locationInstallationPath, summaryInstallationPath } from '../../routes';
import { locationApi } from '../../api/location.api';
import { installationApi } from '../../api/installation.api';
import { deviceApi } from '../../api/device';
import { installationStateApi } from '../../api/installation-state.api';
import { colors } from '../../theme/colors';
import useAuthStore from '../../stores/auth.store';
import useTenantStore from '../../stores/tenant.store';
import { installationStateLabel } from '../../utils/mapTranslationEnums';
import InstallationLocationBuildingDetailInfo from '../../components/installation-location/InstallationLocationDetail/InstallationLocationBuildingDetailInfo';
import { InstallationLocationBuildingFormValues } from '../../components/installation-location/InstallationLocationDetail/FormBuildingData';

const { Title, Text } = Typography;

interface LocationInstallationDetailInfoInterface {
  uuid: string;
  erpId: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  statusColor: string;
  statusName: string;
  numInstallations: string;
  numDevices: string;
  latitude: string;
  longitude: string;
  tenantUuid: string;
  adminUuid?: string;
  numberOfApartments?: number;
  numberOfFloors?: number;
  numberOfCommercialUnits?: number;
  buildingAreaM2?: number;
  averageAge?: number;
  numberOfPeople?: number;
  roofAreaM2?: number;
  hasRainwaterHarvesting?: boolean;
}

const InstallationLocationDetailPage = () => {
  const { installationLocationUuid } = useParams<{ installationLocationUuid: string }>();
  const { t } = useTranslation('installation-location', { keyPrefix: 'installationLocationDetail' });
  const navigate = useNavigate();

  const [locationInfo, setLocationInfo] = useState<LocationInstallationDetailInfoInterface | null>(null);
  const [installations, setInstallations] = useState<InstallationTableItem[]>([]);
  const [loadingInstallations, setLoadingInstallations] = useState(false);

  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const userUuid = useAuthStore((state) => state.uuid);
  const isAdminOfLocation = isAdmin && locationInfo?.adminUuid === userUuid;
  const selectedTenant = useTenantStore((state) => state.selectedTenant);
  const [messageApi, contextHolder] = message.useMessage();

  const prevSelectedTenantRef = useRef(selectedTenant);
  useEffect(() => {
    if (prevSelectedTenantRef.current === selectedTenant) return;
    prevSelectedTenantRef.current = selectedTenant;
    navigate(`/${dashboardPath}/${locationInstallationPath}`);
  }, [selectedTenant, navigate]);

  const fetchInstallations = useCallback(async () => {
    if (!installationLocationUuid) return;
    setLoadingInstallations(true);
    try {
      const [installations, worstStateResponse] = await Promise.all([
        installationApi.getInstallationByLocationUuid(installationLocationUuid),
        locationApi.getAllLocationsWithInstallationWorstState().catch(() => ({ locations: [] })),
      ]);
      if (installations.length === 0) {
        setInstallations([]);
        setLocationInfo((prev) => (prev ? { ...prev, numInstallations: '0', numDevices: '0' } : prev));
        return;
      }
      const locationWorstState =
        worstStateResponse.locations.find((item) => item.location?.uuid === installationLocationUuid)?.state ?? null;
      const states = await Promise.all(
        installations.map((inst) =>
          inst.installationStateUuid
            ? installationStateApi.getByUuid(inst.installationStateUuid).catch(() => null)
            : Promise.resolve(null),
        ),
      );
      const deviceCounts = await Promise.all(
        installations.map((inst) =>
          deviceApi
            .getDeviceByInstallation(inst.uuid)
            .then((d) => d.length)
            .catch(() => 0),
        ),
      );
      const items: InstallationTableItem[] = installations.map((inst, i) => ({
        uuid: inst.uuid,
        id: inst.erpId,
        name: inst.name,
        numDevices: deviceCounts[i],
        statusColor: states[i]?.color ?? colors.NO_STATE_COLOR,
        statusName: installationStateLabel(states[i] ?? null),
        percentageSiphon: inst.siphonCollapseLimitPercentage ?? 0,
      }));
      setInstallations(items);
      const totalDevices = deviceCounts.reduce((sum, count) => sum + count, 0);
      setLocationInfo((prev) =>
        prev
          ? {
              ...prev,
              numInstallations: String(installations.length),
              numDevices: String(totalDevices),
              statusColor: locationWorstState?.color ?? colors.NO_STATE_COLOR,
              statusName: installationStateLabel(locationWorstState),
            }
          : prev,
      );
    } finally {
      setLoadingInstallations(false);
    }
  }, [installationLocationUuid]);

  useEffect(() => {
    if (!installationLocationUuid) return;

    const fetchData = async () => {
      setLoadingInstallations(true);
      try {
        const [loc, installations, worstStateResponse] = await Promise.all([
          locationApi.getByUuid(installationLocationUuid),
          installationApi.getInstallationByLocationUuid(installationLocationUuid),
          locationApi.getAllLocationsWithInstallationWorstState().catch(() => ({ locations: [] })),
        ]);

        if (installations.length === 0) {
          setInstallations([]);
          setLocationInfo({
            uuid: loc.uuid,
            erpId: loc.erpId,
            name: loc.name,
            address: loc.address,
            postalCode: loc.cp,
            city: loc.city,
            latitude: String(loc.latitude),
            longitude: String(loc.longitude),
            numInstallations: '0',
            statusColor: colors.WHITE,
            statusName: installationStateLabel(null),
            numDevices: '0',
            tenantUuid: loc.tenantUuid,
            adminUuid: loc.adminUuid || undefined,
            numberOfApartments: Number(loc.numberOfApartments ?? NaN),
            numberOfFloors: Number(loc.numberOfFloors ?? NaN),
            numberOfCommercialUnits: Number(loc.numberOfCommercialUnits ?? NaN),
            buildingAreaM2: Number(loc.buildingAreaM2 ?? NaN),
            averageAge: Number(loc.averageAge ?? NaN),
            numberOfPeople: Number(loc.numberOfPeople ?? NaN),
            roofAreaM2: Number(loc.roofAreaM2 ?? NaN),
            hasRainwaterHarvesting: loc.hasRainwaterHarvesting,
          });
          return;
        }

        const locationWorstState =
          worstStateResponse.locations.find((item) => item.location?.uuid === installationLocationUuid)?.state ?? null;

        const [states, deviceCounts] = await Promise.all([
          Promise.all(
            installations.map((inst) =>
              inst.installationStateUuid
                ? installationStateApi.getByUuid(inst.installationStateUuid).catch(() => null)
                : Promise.resolve(null),
            ),
          ),
          Promise.all(
            installations.map((inst) =>
              deviceApi
                .getDeviceByInstallation(inst.uuid)
                .then((d) => d.length)
                .catch(() => 0),
            ),
          ),
        ]);

        const items: InstallationTableItem[] = installations.map((inst, i) => ({
          uuid: inst.uuid,
          id: inst.erpId,
          name: inst.name,
          numDevices: deviceCounts[i],
          statusColor: states[i]?.color ?? colors.NO_STATE_COLOR,
          statusName: installationStateLabel(states[i] ?? null),
          percentageSiphon: inst.siphonCollapseLimitPercentage ?? 0,
        }));

        const totalDevices = deviceCounts.reduce((sum, count) => sum + count, 0);

        setInstallations(items);
        setLocationInfo({
          uuid: loc.uuid,
          erpId: loc.erpId,
          name: loc.name,
          address: loc.address,
          postalCode: loc.cp,
          city: loc.city,
          latitude: String(loc.latitude),
          longitude: String(loc.longitude),
          numInstallations: String(installations.length),
          statusColor: locationWorstState?.color ?? colors.NO_STATE_COLOR,
          statusName: installationStateLabel(locationWorstState),
          numDevices: String(totalDevices),
          tenantUuid: loc.tenantUuid,
          adminUuid: loc.adminUuid || undefined,
          numberOfApartments: Number(loc.numberOfApartments ?? NaN),
          numberOfFloors: Number(loc.numberOfFloors ?? NaN),
          numberOfCommercialUnits: Number(loc.numberOfCommercialUnits || NaN),
          buildingAreaM2: Number(loc.buildingAreaM2 ?? NaN),
          averageAge: Number(loc.averageAge ?? NaN),
          numberOfPeople: Number(loc.numberOfPeople ?? NaN),
          roofAreaM2: Number(loc.roofAreaM2 ?? NaN),
          hasRainwaterHarvesting: loc.hasRainwaterHarvesting,
        });
      } catch (error) {
        messageApi.error(t('common:errors.generic'));
      } finally {
        setLoadingInstallations(false);
      }
    };

    fetchData();
  }, [installationLocationUuid, t, messageApi]);

  const handleUpdate = async (values: UpdateLocationParams) => {
    try {
      await locationApi.update({
        ...values,
        numberOfApartments: locationInfo?.numberOfApartments,
        numberOfFloors: locationInfo?.numberOfFloors,
        numberOfCommercialUnits: locationInfo?.numberOfCommercialUnits,
        buildingAreaM2: locationInfo?.buildingAreaM2,
        averageAge: locationInfo?.averageAge,
        numberOfPeople: locationInfo?.numberOfPeople,
        roofAreaM2: locationInfo?.roofAreaM2,
        hasRainwaterHarvesting: locationInfo?.hasRainwaterHarvesting,
      });
      if (!installationLocationUuid) return;
      const loc = await locationApi.getByUuid(installationLocationUuid);
      setLocationInfo((prev) =>
        prev
          ? {
              ...prev,
              name: loc.name,
              erpId: loc.erpId,
              address: loc.address,
              postalCode: loc.cp,
              city: loc.city,
              latitude: String(loc.latitude),
              longitude: String(loc.longitude),
              tenantUuid: loc.tenantUuid,
              adminUuid: loc.adminUuid || undefined,
              numberOfApartments: Number(loc.numberOfApartments ?? NaN),
              numberOfFloors: Number(loc.numberOfFloors ?? NaN),
              numberOfCommercialUnits: Number(loc.numberOfCommercialUnits ?? NaN),
              buildingAreaM2: Number(loc.buildingAreaM2 ?? NaN),
              averageAge: Number(loc.averageAge ?? NaN),
              numberOfPeople: Number(loc.numberOfPeople ?? NaN),
              roofAreaM2: Number(loc.roofAreaM2 ?? NaN),
              hasRainwaterHarvesting: loc.hasRainwaterHarvesting,
            }
          : prev,
      );
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    }
  };

  const handleUpdateBuilding = async (values: InstallationLocationBuildingFormValues) => {
    if (!locationInfo || !installationLocationUuid) return;
    try {
      await locationApi.update({
        uuid: locationInfo.uuid,
        name: locationInfo.name,
        erp_id: locationInfo.erpId,
        address: locationInfo.address,
        cp: locationInfo.postalCode,
        city: locationInfo.city,
        country: 'ES',
        latitude: parseFloat(locationInfo.latitude),
        longitude: parseFloat(locationInfo.longitude),
        tenantUuid: locationInfo.tenantUuid,
        adminUuid: locationInfo.adminUuid ?? '',
        ...values,
      });
      const loc = await locationApi.getByUuid(installationLocationUuid);
      setLocationInfo((prev) =>
        prev
          ? {
              ...prev,
              numberOfApartments: Number(loc.numberOfApartments || NaN),
              numberOfFloors: Number(loc.numberOfFloors || NaN),
              numberOfCommercialUnits: Number(loc.numberOfCommercialUnits || NaN),
              buildingAreaM2: Number(loc.buildingAreaM2 || NaN),
              averageAge: Number(loc.averageAge || NaN),
              numberOfPeople: Number(loc.numberOfPeople || NaN),
              roofAreaM2: Number(loc.roofAreaM2 || NaN),
              hasRainwaterHarvesting: loc.hasRainwaterHarvesting,
            }
          : prev,
      );
    } catch (error) {
      messageApi.error(t('common:errors.generic'));
    }
  };

  const onRowClick = (record: InstallationTableItem) => {
    navigate(`${installationDetailPath}/${record.uuid}/${summaryInstallationPath}`);
  };

  return (
    <Flex vertical>
      {contextHolder}
      <br />
      <div className={'InstallationLocationDetailPage__header'}>
        <div className={'InstallationLocationDetailPage__header__title'}>
          <PartitionOutlined className={'InstallationLocationDetailPage__icon'} />
          <Title level={1} className={'InstallationLocationDetailPage__title'}>
            <Link to={`/${dashboardPath}/${locationInstallationPath}`}>{t('title')}</Link>
            {' / '}
            <span className={'InstallationLocationDetailPage__title__current'}>{locationInfo?.erpId}</span>
          </Title>
        </div>
      </div>
      <br />
      {locationInfo && <InstallationLocationDetailInfo {...locationInfo} onSave={handleUpdate} />}
      <br />
      {locationInfo && <InstallationLocationBuildingDetailInfo {...locationInfo} onSave={handleUpdateBuilding} />}
      <div className={'InstallationLocationDetailPage__createInstallation'}>
        <Text className={'InstallationLocationDetailPage__createInstallation__title'}>{t('installations')}</Text>
        {(isSuperAdminOrCompanyAdmin || isAdminOfLocation) && (
          <CreateInstallation locationUuid={installationLocationUuid} onSuccess={fetchInstallations} />
        )}
      </div>
      <InstallationTable
        pagination={{}}
        onRowClick={onRowClick}
        data={installations}
        loading={loadingInstallations}
        onInstallationUpdated={fetchInstallations}
        isAdminOfLocation={isAdminOfLocation}
      />
    </Flex>
  );
};

export default InstallationLocationDetailPage;
